import React, { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { authService } from "@/services/authService";
import { setCredentials } from "@/store/slices/authSlice";
import { isOwnerRole } from "@/utils/roles";
import Layout from "@/components/Layout";


const Balance = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("2025-07-21");
  const [dateTo, setDateTo] = useState("2025-07-28");

  // Format number helper (matching UpperTab)
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    const numericValue = Number(value) || 0;
    return numericValue.toLocaleString();
  };

  // Fetch profile to refresh wallet data
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const activeRole = localStorage.getItem('activeRole');
      const profileResponse = isOwnerRole(activeRole)
        ? await authService.getOwnerProfile()
        : await authService.getProfile();

      if (profileResponse && profileResponse.success) {
        const userData = isOwnerRole(activeRole)
          ? profileResponse.data
          : profileResponse.data?.user || profileResponse.data?.staff;

        if (userData) {
          const storedToken = isOwnerRole(activeRole)
            ? localStorage.getItem('ownerToken')
            : activeRole === 'staff'
              ? localStorage.getItem('staffToken')
              : localStorage.getItem('token');
          
          dispatch(setCredentials({
            token: storedToken,
            user: userData,
            role: activeRole || 'staff'
          }));
        }
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    } finally {
      setLoading(false);
    }
  };

  // Extract values - matching UpperTab logic
  // UpperTab: total_exposure, downlineBalance, downlineExposure, credit/walletCredit
  
  const netExposure = user?.total_exposure
    ? formatNumber(Math.abs(user.total_exposure))
    : '0';
    
  const balanceDown = user?.downlineBalance 
    ? formatNumber(user.downlineBalance) 
    : '0';
    
  const balanceUp = user?.balanceUpline 
    ? formatNumber(user.balanceUpline) 
    : '0';

  // Available Credit logic from UpperTab: user.credit ?? user.wallet.credit
  const availableCreditRaw = user?.credit ?? user?.wallet?.credit;
  const availableCredit = formatNumber(availableCreditRaw);

  // Credit Limit
  const creditLimit = user?.credit_limit ? formatNumber(user.credit_limit) : '0';


  // Static data for demonstration - 20 items (unchanged)
  const staticData = [
    {
      id: "1",
      date: "2025-07-28",
      description: "Deposit - Bank Transfer",
      pl: "+$1,000.00",
      creditLimit: "$10,000.00",
      balance: "$5,250.00"
    },
    {
      id: "2",
      date: "2025-07-28",
      description: "Bet Settlement - Cricket Match",
      pl: "-$150.00",
      creditLimit: "$10,000.00",
      balance: "$4,100.00"
    },
    {
      id: "3",
      date: "2025-07-27",
      description: "Withdrawal - Online Transfer",
      pl: "-$500.00",
      creditLimit: "$10,000.00",
      balance: "$4,250.00"
    },
    {
      id: "4",
      date: "2025-07-27",
      description: "Bonus Credit - Welcome Bonus",
      pl: "+$200.00",
      creditLimit: "$10,000.00",
      balance: "$4,750.00"
    },
    {
      id: "5",
      date: "2025-07-26",
      description: "Bet Settlement - Football Match",
      pl: "+$350.00",
      creditLimit: "$10,000.00",
      balance: "$4,550.00"
    },
    {
      id: "6",
      date: "2025-07-26",
      description: "Service Fee - Transaction",
      pl: "-$25.00",
      creditLimit: "$10,000.00",
      balance: "$4,200.00"
    },
    {
      id: "7",
      date: "2025-07-25",
      description: "Deposit - Credit Card",
      pl: "+$750.00",
      creditLimit: "$10,000.00",
      balance: "$4,225.00"
    },
    {
      id: "8",
      date: "2025-07-25",
      description: "Bet Settlement - Tennis Match",
      pl: "-$300.00",
      creditLimit: "$10,000.00",
      balance: "$3,475.00"
    },
    {
      id: "9",
      date: "2025-07-24",
      description: "Referral Bonus - New User",
      pl: "+$100.00",
      creditLimit: "$10,000.00",
      balance: "$3,775.00"
    },
    {
      id: "10",
      date: "2025-07-24",
      description: "Bet Settlement - Basketball",
      pl: "+$450.00",
      creditLimit: "$10,000.00",
      balance: "$3,675.00"
    },
    {
      id: "11",
      date: "2025-07-23",
      description: "Withdrawal - Bank Transfer",
      pl: "-$800.00",
      creditLimit: "$10,000.00",
      balance: "$3,225.00"
    },
    {
      id: "12",
      date: "2025-07-23",
      description: "Bet Settlement - Cricket ODI",
      pl: "-$200.00",
      creditLimit: "$10,000.00",
      balance: "$4,025.00"
    },
    {
      id: "13",
      date: "2025-07-22",
      description: "Deposit - E-Wallet",
      pl: "+$600.00",
      creditLimit: "$10,000.00",
      balance: "$4,225.00"
    },
    {
      id: "14",
      date: "2025-07-22",
      description: "Bet Settlement - Football League",
      pl: "+$275.00",
      creditLimit: "$10,000.00",
      balance: "$3,625.00"
    },
    {
      id: "15",
      date: "2025-07-21",
      description: "Service Fee - Monthly",
      pl: "-$50.00",
      creditLimit: "$10,000.00",
      balance: "$3,350.00"
    },
    {
      id: "16",
      date: "2025-07-21",
      description: "Bet Settlement - Tennis Grand Slam",
      pl: "-$400.00",
      creditLimit: "$10,000.00",
      balance: "$3,400.00"
    },
    {
      id: "17",
      date: "2025-07-20",
      description: "Deposit - Bank Transfer",
      pl: "+$1,200.00",
      creditLimit: "$10,000.00",
      balance: "$3,800.00"
    },
    {
      id: "18",
      date: "2025-07-20",
      description: "Bet Settlement - Basketball Finals",
      pl: "+$600.00",
      creditLimit: "$10,000.00",
      balance: "$2,600.00"
    },
    {
      id: "19",
      date: "2025-07-19",
      description: "Withdrawal - Online Banking",
      pl: "-$300.00",
      creditLimit: "$10,000.00",
      balance: "$2,000.00"
    },
    {
      id: "20",
      date: "2025-07-19",
      description: "Bet Settlement - Cricket T20",
      pl: "-$150.00",
      creditLimit: "$10,000.00",
      balance: "$2,300.00"
    }
  ];

  // Filter data based on search and date range
  const filteredData = useMemo(() => {
    return staticData.filter(item => {
      const matchesSearch = searchTerm === "" || 
        item.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const itemDate = new Date(item.date);
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      
      const matchesDateRange = itemDate >= fromDate && itemDate <= toDate;
      
      return matchesSearch && matchesDateRange;
    });
  }, [searchTerm, dateFrom, dateTo]);

  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFrom, dateTo]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEntriesPerPageChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  return (
    <div className="h-full p-2 bg-[#0f1224] border-[15px] border-[#171b36]">
      <div className="w-full mx-auto">
        {/* Balance Information Panel */}
        <div className="rounded-lg p-2">
          <div className="flex justify-between items-center mb-2">
            <h2 style={{color: '#cae0e8', fontSize: '24px'}} className="text-[24px] font-semibold text-[#cae0e8]">Balance Information</h2>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="bg-[#11374a] hover:bg-[#1a4f66] text-white text-[12px] font-medium px-3 py-1 transition-colors disabled:opacity-50"
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          <div style={{borderColor: '#cae0e8', borderBottomWidth: '1px', borderBottomStyle: 'solid'}} className="mb-2" />
          <div className="space-y-1 w-[20%]">
            <div className="flex justify-between items-center py-1 px-2 bg-[#252837] mb-1 ">
              <span className="text-white text-[14px] font-bold">Net Exposure</span>
              {/* Using same color logic as UpperTab if possible, defaulting to UpperTab colors: 
                  UpperTab: Net Exposure -> text-[#3cd247] (Greenish)
              */}
              <span className="text-[#3cd247] text-[14px] font-bold">
                {netExposure}
              </span>
            </div>
            <div className="flex justify-between items-center px-2 mb-1 ">
              <span className="text-white text-[14px] font-bold">Balance Down</span>
              {/* UpperTab: Balance Down -> text-[#3cd247] */}
              <span className="text-[#3cd247] text-[14px] font-bold">{balanceDown}</span>
            </div>
            <div className="flex justify-between items-center py-1 px-2 bg-[#252837] mb-1 ">
              <span className="text-white text-[14px] font-bold">Balance Up</span>
              {/* UpperTab: Balance Up -> text-[#f43] (Red) */}
              <span className="text-[#f43] text-[14px] font-bold">{balanceUp}</span>
            </div>
            {/* Keeping Credit Limit for now, but styling it consistent */}
            <div className="flex justify-between items-center px-2 mb-1 ">
              <span className="text-white text-[14px] font-bold">Credit Limit</span>
               {/* No specific color map in UpperTab for Credit Limit, using Red like previous or default? 
                   Previous was red-500. Let's make it standard red or white? 
                   UpperTab Doesn't have this. Let's keep it red-500 from previous to be safe.
               */}
              <span className="text-red-500 text-[14px] font-bold">{creditLimit}</span>
            </div>
            <div className="flex justify-between items-center py-1 px-2 bg-[#252837] mb-1 ">
              <span className="text-white text-[14px] font-bold">Available Credit</span>
              {/* UpperTab Available Credit -> text-white */}
              <span className="text-white text-[14px] font-bold">{availableCredit}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function DashboardHome() {
    return (
      <Layout>
            <Balance />
        </Layout>
    )
}